import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Inject,
  Optional,
  Type,
} from '@angular/core';
import { BuildContentService } from '../../service/build-content';
import { groupNodesBySelector } from './helpers';
import { CONTENT_COMPONENTS, CONTENT_PREFORMATTERS } from '../../const';
import { timer } from 'rxjs';
import { takeUntilDestroyed, DecorateUntilDestroy } from '@skyeng/libs/shared/rxjs';
import { vimCoreSentry } from '@skyeng/libs/vimbox-core/sentry';
import { BlocksContentPreformatter, LinkedNgElement } from '../../interface';
import { BuildError } from '../../error/build-error';
import { getDomPath } from '../../helpers';

@Component({
  selector: 'sky-block-build-content',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@DecorateUntilDestroy()
export class BuildContentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() content?: string;

  @Output() compilationEnd = new EventEmitter<void>();
  @Output() compilationStart = new EventEmitter<void>();
  @Output() compilationError = new EventEmitter<void>();

  private addedComponents: ComponentRef<unknown>[] = [];
  private componentsInjectorsByElement = new WeakMap<Node, Injector>();
  private hostElement: HTMLElement;
  private preformattedContent?: string;

  constructor(
    private applicationRef: ApplicationRef,
    private elementRef: ElementRef,
    private injector: Injector,
    private buildContentService: BuildContentService,
    @Inject(CONTENT_COMPONENTS) @Optional() private contentComponents?: Type<unknown>[][],
    @Inject(CONTENT_PREFORMATTERS) @Optional() private contentPreformatters?: BlocksContentPreformatter[][],
  ) {}

  ngOnInit(): void {
    this.buildContentService.storeContentComponentsFactories(this.contentComponents);
    this.buildContentService.storeContentPreformatters(this.contentPreformatters);

    this.hostElement = this.elementRef.nativeElement as HTMLElement;

    this.rebuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.hostElement && changes.content) {
      this.rebuild();
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  rebuild(): void {
    try {
      this.build();
    } catch (error) {
      this.compilationError.emit();

      throw error;
    }
  }

  getPreformattedContent(): string | undefined {
    return this.preformattedContent;
  }

  private build(): void {
    const content = this.buildContentService.preformatContent(this.content);

    this.preformattedContent = content;

    this.compilationStart.emit();

    this.cleanup();

    if (!content) {
      this.compilationEnd.emit();
      return;
    }

    // Since sky-block-build-content tag uses in many places, we should integrate solution in "soft" manner:
    // - Trying to render components in a new way
    // - If we faced with NullInjectorError error then trying to re-render with legacy code.
    //
    // After some time, if we wouldn't have issues in Sentry and requests from vim-bugs,
    // we should remove legacy rendering logic and stay only one version.
    try {
      this.createComponents(content);
    } catch (error) {
      console.warn(error);
      console.log('[sky-block-build-content] Switch to legacy rendering');
      vimCoreSentry.captureException(error);

      // If we have some problem with current solution, then trying to do fallback rendering
      this.cleanup();
      this.legacyCreateComponents(content);
    }

    for (const component of this.addedComponents) {
      component.changeDetectorRef.detectChanges();
    }

    timer(0)
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.compilationEnd.emit());
  }

  private cleanup(): void {
    // NOTE: reverse for destroying order from child to parents
    this.addedComponents.reverse().forEach(component => {
      this.applicationRef.detachView(component.hostView);
      component.changeDetectorRef.detach();
      component.destroy();
    });
    this.addedComponents = [];
    this.componentsInjectorsByElement = new WeakMap<Node, Injector>();
  }

  private legacyCreateComponents(content: string): void {
    // eslint-disable-next-line functional/immutable-data
    this.hostElement.innerHTML = content || '';

    // default injector for first level components
    this.componentsInjectorsByElement.set(this.hostElement, this.injector);

    const contentDomElements = Array.from(this.hostElement.getElementsByTagName('*'));
    let previousComponentElement: Node | null = null;

    for (const domElement of contentDomElements) {
      try {
        const componentFactory = this.buildContentService.getContentComponentFactory(domElement.tagName);

        if (!componentFactory) {
          continue;
        }

        const attributes = Array.from(domElement.attributes);
        const injector = this.getParentInjector(domElement, previousComponentElement);
        const projectableNodes = groupNodesBySelector(componentFactory.ngContentSelectors, domElement);

        const contentComponent = componentFactory.create(injector, projectableNodes, domElement);

        for (const attribute of attributes) {
          const inputName = this.normalizeInputName(attribute.nodeName);

          // eslint-disable-next-line functional/immutable-data
          contentComponent.setInput(inputName, attribute.nodeValue);

          // remove attribute for clean DOM
          domElement.attributes.removeNamedItem(attribute.nodeName);
        }

        this.addedComponents.push(contentComponent);

        this.applicationRef.attachView(contentComponent.hostView);

        this.componentsInjectorsByElement.set(domElement, contentComponent.injector);

        previousComponentElement = domElement;
      } catch (error) {
        throw new BuildError(error as Error, {
          tagName: domElement.tagName,
          tagDomPath: getDomPath(domElement),
          documentMarkup: content,
        });
      }
    }
  }

  private createComponents(content: string): void {
    // eslint-disable-next-line functional/immutable-data
    this.hostElement.innerHTML = content || '';

    // default injector for first level components
    this.componentsInjectorsByElement.set(this.hostElement, this.injector);

    const ngElements = this.getLinkedNgElementsFromHostElement(this.hostElement);

    // Creation logic is the same as legacy logic used
    for (const { element, parent } of ngElements) {
      try {
        const componentFactory = this.buildContentService.getContentComponentFactory(element.tagName);

        if (!componentFactory) {
          continue;
        }

        const attributes = Array.from(element.attributes);
        const injector = this.componentsInjectorsByElement.get(parent as Node) ?? this.injector;
        const projectableNodes = groupNodesBySelector(componentFactory.ngContentSelectors, element);

        const contentComponent = componentFactory.create(injector, projectableNodes, element);

        for (const attribute of attributes) {
          const inputName = this.normalizeInputName(attribute.nodeName);

          // eslint-disable-next-line functional/immutable-data
          contentComponent.setInput(inputName, attribute.nodeValue);

          // remove attribute for clean DOM
          element.attributes.removeNamedItem(attribute.nodeName);
        }

        this.addedComponents.push(contentComponent);

        this.applicationRef.attachView(contentComponent.hostView);

        this.componentsInjectorsByElement.set(element, contentComponent.injector);
      } catch (error) {
        throw new BuildError(error as Error, {
          documentMarkup: content,
          tagName: element.tagName,
          tagDomPath: getDomPath(element),
          tagOuterMarkup: parent.innerHTML,
        });
      }
    }
  }

  private getParentInjector(element: Node | null, previousComponentElement: Node | null): Injector {
    while (element && element !== this.hostElement) {
      element = element.parentNode;

      const elementInjector = this.componentsInjectorsByElement.get(element as Node);
      if (elementInjector) {
        return elementInjector;
      }
    }

    // NOTE: current element can be moved by parent's content projection
    // assume that previous instantiated element is that parent and use his injector
    //
    // NOTE2: It's logic broken when we have next example case:
    // teacher-notes (1)
    //   vim-audio
    //     teacher-notes (2)
    //       vim-transcript-cue
    //       ...
    //       vim-transcript-cue <- here using teacher-notes2 and seems like all good
    //   vim-dnd-text <- Here we should to use teacher-notes1, but would be teacher-notes2.
    //                   It happens since it pass from previousComponentElement injector
    //     ...
    return this.componentsInjectorsByElement.get(previousComponentElement as Node) || this.injector;
  }

  // kebab-case -> camelCase
  private normalizeInputName(attributeName: string): string {
    return attributeName.replace(
      /-([^-]+)/g,
      (_, namePart: string) => namePart.charAt(0).toUpperCase() + namePart.slice(1),
    );
  }

  /**
   * Method returns list of elements that components needs to be resolved and created.
   * Each element also has "parent" property needing to pick correct injector
   *
   * @param hostElement
   * @private
   */
  private getLinkedNgElementsFromHostElement(hostElement: HTMLElement): LinkedNgElement[] {
    const stack: [HTMLElement, HTMLElement | null][] = [[hostElement, null]];

    const result: LinkedNgElement[] = [];

    // As DOM traversing uses Depth-First-Search algorithm.
    // The same algo uses in standart "getElementsByTagName" function.
    //
    // But with some additional optimization:
    // as a parent element uses closest ng-element for set correctly injector in future creation phase
    while (stack.length > 0) {
      // eslint-disable-next-line functional/immutable-data
      const [currentNode, parentNode]: [HTMLElement, HTMLElement | null] = stack.pop()!;

      // Skip rootElement and any non angular elements
      if (parentNode !== null && this.buildContentService.hasContentComponentFactory(currentNode.tagName)) {
        // eslint-disable-next-line functional/immutable-data
        result.push({ element: currentNode, parent: parentNode });
      }

      if (currentNode?.children?.length > 0) {
        const currentParentNode = this.getClosestNgElementParent(currentNode) ?? hostElement;

        for (let i = currentNode?.children?.length - 1; i >= 0; i--) {
          const childNode = currentNode.children[i] as HTMLElement;

          if (!([Node.ELEMENT_NODE, Node.ENTITY_NODE] as number[]).includes(childNode.nodeType)) {
            continue;
          }

          // eslint-disable-next-line functional/immutable-data
          stack.push([childNode, currentParentNode]);
        }
      }
    }

    return result;
  }

  private getClosestNgElementParent(parentNode: HTMLElement): HTMLElement | null {
    let currentParentNode = parentNode;

    while (currentParentNode) {
      if (this.buildContentService.hasContentComponentFactory(currentParentNode.tagName)) {
        return currentParentNode;
      }

      currentParentNode = currentParentNode.parentElement as HTMLElement;
    }

    return null;
  }
}
